import { useState, useEffect, useCallback } from 'react';
import { API } from 'aws-amplify';
import { GET_ORG_TEMPLATES } from '../graphql/queries';
import { AiGuidance } from '../aiguidanceUtils';

export interface TemplateItem {
  id: string;
  name: string;
  description?: string;
  taskType: string;
  itemKey?: string;
  sortOrder: number;
  zoneId?: string;
  isDeleted?: boolean;
  aiGuidance?: AiGuidance | null;
  imageName?: string | null;
}

export interface Template {
  id: string;
  name: string;
  description?: string;
  orgId?: string;
  isSystem: boolean;
  status: string;
  version: number;
  parentTemplateId?: string;
  serviceCategory?: string;
  items: TemplateItem[];
  findingTypes: any[];
  zones: any[];
}

export default function useOrgTemplates() {
  const orgID = (global as any)?.me?.orgID;
  const [templates, setTemplates] = useState<Template[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(
    async (showLoading = true) => {
      if (!orgID) return;

      try {
        if (showLoading) setIsLoading(true);
        setError(null);

        const result: any = await API.graphql({
          query: GET_ORG_TEMPLATES as any,
          variables: {
            request: JSON.stringify({ orgId: orgID }),
          },
        });

        const parsed = JSON.parse(result.data.getOrgTemplates);

        if (parsed.success) {
          setTemplates(parsed.data?.templates ?? []);
        } else {
          setError(parsed.error ?? 'Failed to fetch templates');
        }
      } catch (err: any) {
        console.error('Error fetching org templates:', err);
        setError(err?.message ?? 'Failed to fetch templates');
      } finally {
        setIsLoading(false);
      }
    },
    [orgID]
  );

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { templates, isLoading, error, refetch: () => fetchTemplates(false) };
}
